import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import {
  Paper,
  Button,
  Input,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { searchButtonSx } from 'components/searchButtonStyles';

const useStyles = makeStyles((theme) => ({
  searchByAllFields: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    paddingTop: 7,
  },
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    flexGrow: 1,
    height: 42,
    padding: theme.spacing(0, 2),
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.icon,
  },
  searchInput: {
    flexGrow: 1,
  },
  searchButton: {
    marginLeft: theme.spacing(2),
    minWidth: '103.96px',
  },
  timeSelect: {
    backgroundColor: theme.palette.common.white,
    maxHeight: '42px',
    '& .MuiSelect-select:focus ': {
      backgroundColor: 'transparent',
    },
    '& fieldset': { border: 'none' },
  },
}));

function SessionSearchBar({ onSearch, className, configuration, ...rest }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const parseBooleanParam = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      return normalizedValue === 'true' || normalizedValue === '1';
    }
    return false;
  };

  const { values, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      search: '',
      time_filter: 'last_1_months',
      limitedSearch: false,
    },
    enableReinitialize: true,
    onSubmit: onSearch,
  });

  useEffect(() => {
    if (!configuration?.params) return;
    if (configuration.params?.limitedSearch === undefined) return;

    setFieldValue(
      'limitedSearch',
      parseBooleanParam(configuration.params?.limitedSearch),
    );
  }, [setFieldValue, configuration?.params?.limitedSearch]);

  useEffect(() => {
    if (!configuration?.params?.time_filter) return;
    setFieldValue('time_filter', configuration.params?.time_filter);
  }, [setFieldValue, configuration?.params?.time_filter]);

  useEffect(() => {
    if (!configuration?.params?.search) return;
    setFieldValue('search', configuration.params?.search);
  }, [setFieldValue, configuration?.params?.search]);

  return (
    <div>
      <div {...rest} className={clsx(classes.root, className)}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Paper className={classes.search} elevation={1}>
              <SearchIcon className={classes.searchIcon} />
              <Input
                value={values.search}
                onChange={(e) => setFieldValue('search', e.target.value)}
                className={classes.searchInput}
                disableUnderline
                placeholder={t('search')}
              />
            </Paper>
            <Paper elevation={1}>
              <Select
                value={values.time_filter}
                onChange={(e) => setFieldValue('time_filter', e.target.value)}
                variant="outlined"
                className={classes.timeSelect}>
                <MenuItem value="last_1_months">1 {t('month')}</MenuItem>
                <MenuItem value="last_3_months">3 {t('months')}</MenuItem>
                <MenuItem value="last_6_months">6 {t('months')}</MenuItem>
                <MenuItem value="all_time">All Time</MenuItem>
              </Select>
            </Paper>
            <Button
              className={classes.searchButton}
              type="submit"
              size="large"
              sx={searchButtonSx}
              variant="contained">
              {t('search')}
            </Button>
          </div>
          <div className={classes.searchByAllFields}>
            <Switch
              size="small"
              color="primary"
              checked={!!values.limitedSearch}
              onChange={() =>
                setFieldValue('limitedSearch', !values.limitedSearch)
              }
            />
            <Typography variant="body2" component="span">
              {t(
                // values.limitedSearch ?
                'searchByOrderNumber',
                // : 'searchByAllFields',
              )}
            </Typography>
          </div>
        </form>
      </div>
    </div>
  );
}

SessionSearchBar.propTypes = {
  value: PropTypes.string,
  onSearch: PropTypes.func,
  className: PropTypes.string,
};

export default SessionSearchBar;
